<script lang="typescript">
    import { chevronBack, lensOption, upgradeLensImages } from "./appData.js";
    import { state, setState } from "./stores";
    import { MessageType } from "./types";
    import type { LensVariant, Lens } from "./types";
    import {
        computeTotalPrice,
        getPrimaryVariant,
        getSecondaryVariant,
        getTotalPriceFromState,
        getBaseLensObject,
        exchangeVariants,
        getPromoImage,
        getBaseLensImage,
    } from "./utils";
    import { onDestroy } from "svelte";
    import { getVariantProductCodes } from "./utils";
    import { push, pop } from "svelte-spa-router";
    import { fade } from "svelte/transition";
    import LensCardMd from "./LensCardMd.svelte";
    import PriceDisplayXL from "./PriceDisplayXL.svelte";
    import UpgradeCardMd from "./UpgradeCardMd.svelte";

    export let showUpgradeTiers: boolean = false;

    if ($state.rxId === null) window.location.replace("/");

    const selectedLens: Lens = getBaseLensObject($state.focalType)[$state.baseLensKey];

    let primaryVariant: LensVariant | null = getPrimaryVariant($state);
    let secondaryVariant: LensVariant | null = getSecondaryVariant($state);

    let didExchangeVariants = false;
    if (
        primaryVariant !== null &&
        secondaryVariant !== null &&
        primaryVariant.optionType === lensOption.ADAPTIVE &&
        secondaryVariant.optionType === lensOption.ANTIFOG
    ) {
        setState({
            message: MessageType.SET_VARIANTS,
            ...exchangeVariants($state),
        });
        didExchangeVariants = true;
        primaryVariant = getPrimaryVariant($state);
        secondaryVariant = getSecondaryVariant($state);
    }

    const totalPrice: number = getTotalPriceFromState($state);

    const handleGoBack = () => {
        setState({ message: MessageType.RESET_VARIANTS });
        pop();
    };

    onDestroy(() => {
        if (
            didExchangeVariants &&
            $state.primaryVariantIndex !== null &&
            $state.secondaryVariantIndex !== null
        ) {
            setState({
                message: MessageType.SET_VARIANTS,
                ...exchangeVariants($state),
            });
        }
        window.scrollTo(0, 0);
    });

    // for Transitions with anti-reflect on Varifocals 0-2, upto 2+ essential the product codes should be on the same page with both prices.
    let antiReflect: LensVariant | null = null;
    let antiReflectPrice: number | null = null;
    if (
        $state.baseLensKey === "ESSENTIAL" &&
        primaryVariant !== null &&
        primaryVariant.optionType === lensOption.TRANSITIONS
    ) {
        antiReflect = primaryVariant.secondaryVariants[0];
        antiReflectPrice = computeTotalPrice(
            getBaseLensObject($state.focalType),
            $state.baseLensKey,
            primaryVariant,
            antiReflect,
            $state.region
        );
    }

    const getLensImage = (lens: Lens) => {
        if ($state.upgradeLensKey) {
            return upgradeLensImages[$state.lensTier].src;
        }
        return getBaseLensImage(lens, $state.region);
    };

    $: image = getLensImage(selectedLens);
</script>

<section class="flex-row items-start flex-wrap justify-center" in:fade>
    <div class="page-container sm:flex-row items-stretch justify-center">
        <div class="column space-y-3 justify-start w-full pl-2 lg:pl-0">
            <h1 class="font-normal text-3xl lg:text-5xl mb-2 pl-2 lg:pl-0 mr-auto tracking-tight">
                Your order summary
            </h1>
            {#if selectedLens}
                <LensCardMd {selectedLens} {primaryVariant} {secondaryVariant} {image} />
            {/if}
            {#if primaryVariant !== null && primaryVariant.optionType === lensOption.TINT_UV}
                <span class="font-bold text-5xl">+</span>
                <UpgradeCardMd
                    variant={primaryVariant}
                    codes={getVariantProductCodes(primaryVariant)}
                />
            {/if}
            {#if antiReflect !== null}
                <span class="font-bold text-5xl">+</span>
                <UpgradeCardMd variant={antiReflect} codes={getVariantProductCodes(antiReflect)} />
            {/if}

            <!-- show a message for optimised/individual lenses -->
            {#if $state.upgradeLensKey && $state.lensTier > 0}
                <span class="font-bold text">Don't forget to take lens fitting heights</span>
            {/if}
            <div class="flex justify-between items-start md:items-center w-full xl:pr-8 pt-12">
                <nav class="pl-2">
                    <button
                        class="btn-primary btn-sm mb-2 mr-4 xl:mb-0 px-4 lg:px-8"
                        type="button"
                        on:click={handleGoBack}
                    >
                        {@html chevronBack}Go back
                    </button>
                    <button
                        class="btn-secondary btn-sm px-4 lg:px-8"
                        type="button"
                        on:click={() => push("/")}
                    >
                        Start again
                    </button>
                </nav>

                <PriceDisplayXL price={totalPrice}>
                    <span class="text-4xl font-medium">
                        {totalPrice > 0 ? "Total " : ""}
                    </span>
                    {#if $state.focalType}
                        <br />
                    {/if}
                </PriceDisplayXL>
                {#if antiReflectPrice !== null}
                    <PriceDisplayXL price={antiReflectPrice} classes="pl-8 -mt-8">
                        <span class="text-4xl font-medium">
                            <h2>+ Anti-Reflection</h2>
                            {totalPrice > 0 ? "Total " : ""}
                        </span>
                        {#if $state.focalType}
                            <br />
                        {/if}
                    </PriceDisplayXL>
                {/if}
            </div>
        </div>
        <div class="column justify-between lg:flex-shrink-0 lg:max-w-lg px-2">
            <img
                src={getPromoImage($state)}
                class="promo-image"
                alt="half price second pair promotion"
            />
        </div>
    </div>
</section>

<style>
    .column {
        @apply flex flex-col items-center py-8 lg:px-8;
    }

    .column:first-of-type {
        @apply pr-0;
    }

    .promo-image {
        max-height: calc(100vh - 4rem);
        @apply sticky top-8;
    }
</style>
